import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { parseSalaryValue } from "@/helpers/utils.helper.ts";
import { EmployeeCompensationLines } from "@/pages/employees/employee/components/EmployeeCompensationLines.tsx";
import {
  EmployeeCompensationFormValues,
  EmployeeCompensationLineFormValue,
} from "@/pages/employees/employee/components/employee-compensation-form.types.ts";
import { useCompensationComponents } from "@/queries/use-compensation-components.query.ts";
import { BASIC_SALARY_COMPENSATION_COMPONENT_CODE } from "@/types/compensation-component.types.ts";
import { ApiEmployeeCompensationPerTypes } from "@/types/employees/employee-compensation.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, Text } from "@skailer-com/uikit";
import { Ref, useEffect, useImperativeHandle, useRef } from "react";
import { Controller, Resolver, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { HirePackageState, isHirePackageEmpty } from "../hire-form";

export interface HireCompensationLinesHandle {
  validate: () => Promise<boolean>;
}

export interface HireCompensationLinesProps {
  ref?: Ref<HireCompensationLinesHandle>;
  value: HirePackageState;
  onChange: (value: HirePackageState) => void;
  required: boolean;
  basicSalaryDefaults?: Partial<EmployeeCompensationLineFormValue>;
  defaultCurrency: string;
  serverError?: string;
  disabled?: boolean;
}

const amountToNumber = (value: string | number | null) => {
  const parsed = parseSalaryValue(value);
  return typeof parsed === "number" && !Number.isNaN(parsed) ? parsed : null;
};

const isBlank = (value: string | number | null | undefined) => value === null || value === "";

export function HireCompensationLines({
  ref,
  value,
  onChange,
  required,
  basicSalaryDefaults,
  defaultCurrency,
  serverError,
  disabled,
}: HireCompensationLinesProps) {
  const { t } = useTranslation();
  const { data: components } = useCompensationComponents();
  const basicSalaryComponent = components?.find(
    (component) => component.code === BASIC_SALARY_COMPENSATION_COMPONENT_CODE
  );
  const requiredError = t("errors:field_required");

  const amountSchema = z.union([z.string(), z.number()]).nullable();
  const lineSchema = z
    .object({
      id: z.number().optional(),
      compensation_component_id: z.number({ error: requiredError }).positive(requiredError),
      amount_gross: amountSchema,
      amount_net: amountSchema,
      currency_code: z.string().nullable(),
      per_type: z.enum(ApiEmployeeCompensationPerTypes).nullable(),
      sort: z.number().nullable(),
      description: z.string().nullable(),
    })
    .superRefine((line, context) => {
      (["amount_gross", "amount_net"] as const).forEach((field) => {
        const amount = line[field];
        if (!isBlank(amount) && amountToNumber(amount) === null)
          context.addIssue({
            code: "custom",
            message: t("errors:field_number_required"),
            path: [field],
          });
      });
      if (isBlank(line.amount_gross) && isBlank(line.amount_net))
        (["amount_gross", "amount_net"] as const).forEach((field) =>
          context.addIssue({ code: "custom", message: requiredError, path: [field] })
        );
      if (!line.currency_code)
        context.addIssue({ code: "custom", message: requiredError, path: ["currency_code"] });
      if (!line.per_type)
        context.addIssue({ code: "custom", message: requiredError, path: ["per_type"] });
    });
  const schema = z.looseObject({
    currency: z.string({ error: requiredError }).min(1, requiredError),
    lines: z.array(lineSchema),
  });

  const form = useForm<EmployeeCompensationFormValues>({
    resolver: zodResolver(schema) as unknown as Resolver<EmployeeCompensationFormValues>,
    defaultValues: value.values ?? {
      date_effective_from: new Date(),
      currency: defaultCurrency,
      lines: [],
    },
  });
  const {
    control,
    setValue,
    getValues,
    trigger,
    clearErrors,
    subscribe,
    formState: { errors },
  } = form;
  const { fields, append, remove } = useFieldArray({ control, name: "lines", keyName: "fieldKey" });
  const watchedLines = useWatch({ control, name: "lines" });

  const toState = (values: EmployeeCompensationFormValues): HirePackageState => ({
    values,
    payload: isHirePackageEmpty(values)
      ? null
      : {
          currency: values.currency || null,
          lines: values.lines.map((line) => ({
            compensation_component_id: line.compensation_component_id,
            amount_gross: amountToNumber(line.amount_gross),
            amount_net: amountToNumber(line.amount_net),
            currency_code: line.currency_code,
            per_type: line.per_type,
            sort: line.sort,
            description: line.description?.trim() || null,
          })),
        },
  });

  const latestOnChange = useRef(onChange);
  useEffect(() => {
    latestOnChange.current = onChange;
  });
  useEffect(
    () =>
      subscribe({
        formState: { values: true },
        callback: ({ values }) =>
          latestOnChange.current(toState(values as EmployeeCompensationFormValues)),
      }),
    [subscribe]
  );

  useEffect(() => {
    if (!basicSalaryComponent || fields.length > 0) return;
    append(
      {
        compensation_component_id: basicSalaryComponent.id,
        amount_gross: null,
        amount_net: null,
        currency_code: basicSalaryComponent.default_currency_code ?? defaultCurrency,
        per_type: basicSalaryComponent.default_per_type ?? ApiEmployeeCompensationPerTypes.monthly,
        sort: basicSalaryComponent.sort,
        description: null,
        ...basicSalaryDefaults,
      },
      { shouldFocus: false }
    );
  }, [append, basicSalaryComponent, basicSalaryDefaults, defaultCurrency, fields.length]);

  useEffect(() => {
    if (!getValues("currency") && defaultCurrency) setValue("currency", defaultCurrency);
  }, [defaultCurrency, getValues, setValue]);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!required && isHirePackageEmpty(getValues())) {
        clearErrors();
        return true;
      }
      return trigger();
    },
  }));

  const changeLineComponent = (index: number, componentId: number) => {
    const component = components?.find((item) => item.id === componentId);
    if (!component) return;
    setValue(`lines.${index}.currency_code`, component.default_currency_code ?? defaultCurrency, {
      shouldDirty: true,
    });
    setValue(
      `lines.${index}.per_type`,
      component.default_per_type ?? ApiEmployeeCompensationPerTypes.monthly,
      { shouldDirty: true }
    );
    setValue(`lines.${index}.sort`, component.sort, { shouldDirty: true });
  };

  return (
    <Stack className={disabled ? "opacity-60 pointer-events-none" : undefined}>
      <Controller
        control={control}
        name="currency"
        render={({ field }) => (
          <CurrencySelect
            {...field}
            required
            label={t("common:currency")}
            emptyTitle={t("common:select")}
            searchable
            isClearable
            error={errors.currency?.message}
          />
        )}
      />
      <EmployeeCompensationLines
        fields={fields.map((field, index) => ({ ...field, ...watchedLines?.[index] }))}
        components={components ?? []}
        control={control}
        errors={errors}
        onAdd={() =>
          append({
            compensation_component_id: 0,
            amount_gross: null,
            amount_net: null,
            currency_code: defaultCurrency,
            per_type: ApiEmployeeCompensationPerTypes.monthly,
            sort: (fields.length + 1) * 10,
            description: null,
          })
        }
        onDelete={remove}
        onComponentChange={changeLineComponent}
        onAmountChange={(index) =>
          void trigger([`lines.${index}.amount_gross`, `lines.${index}.amount_net`])
        }
      />
      {serverError && <Text className="text-danger">{serverError}</Text>}
    </Stack>
  );
}
